import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "./Button";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Buttons in the footer. */
  actions?: ReactNode;
}

/** The native <dialog>: focus trapping, Escape and the backdrop come from the browser. */
export function Dialog({ open, onClose, title, children, actions }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-2xl border-2 border-ink bg-card p-0 text-ink shadow-hard-lg open:motion-safe:animate-rise"
    >
      <div className="flex flex-col gap-4 p-6">
        <h2 className="text-xl font-bold">{title}</h2>
        <div className="flex flex-col gap-3">{children}</div>
        {actions && <div className="flex flex-wrap justify-end gap-2 pt-2">{actions}</div>}
      </div>
    </dialog>
  );
}

interface ConfirmProps {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({ open, title, children, confirmLabel, danger, pending, onConfirm, onCancel }: ConfirmProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      actions={<>
        <Button onClick={onCancel}>cancel</Button>
        <Button variant={danger ? "danger" : "primary"} pending={pending} onClick={onConfirm}>{confirmLabel}</Button>
      </>}
    >
      {children}
    </Dialog>
  );
}
